import { AddOnAvailability, CreateChallengeRequest, CreateElectionRequest, CreateTopicRequest, DiscoveryLevel, EmailGateDetails, EntityType, GateDetails, GateType, GetAddOnResponse, GetChallengeResponse, GetElectionResponse, GetPointResponse, GetPopUpResponse, GetTopicEmailGateResponse, GetTopicPasswordGateResponse, GetTopicResponse, IntegrationException, IntentType, KeyLevel, ObjectId, PasswordGateDetails, PopUpTypeOld, SearchBounds, TagRequest, TopicCategory, UserTag } from '../../types';
import { DEFAULT_CATEGORY_COLORS, DEFAULT_CATEGORY_ICON, DEFAULT_TAG_COLORS, dummyInitialElection, high_street_treasure_hunt_add_on } from '../constants';
import { GoogleTagEvent, logWithAmplitude, logWithGoogleTagAndTopicOrganization } from '../api/api';
import { addChallenge, createChallengeRequestFromGetChallengeResponse, getChallenges, updateChallenge } from '../api/challenge-api';
import { addElection, createElectionRequestFromGetElectionResponse, electionNominationsAreHappening, electionResultsAreLive, electionVotingIsHappening, getElections, updateElection } from '../api/election-api';
import { addTopicView, createTopicRequestFromTopic, getTopic, updateTopic } from '../api/topics-api';
import { getDarkerColor, getVisibleTextColor, isTextColorVisible, makeColorIntoDataSvg } from '../colors';
import { getQRCode, getQueryStringParameters } from '../urls';
import { isDefined, isValidString, redirectToPage, replaceStateWithPageName, setFavicon, undefinedIfEmpty, urlToDataUrl } from '../utils';

import { MapComponent } from '../../components/map-component';
import { State } from '../state/state-management/state';
import accountInfo from './account-info';
import challengeData from './challenge-data';
import { deletePoint } from '../api/points-api';
import featuresInfo from './features-info';
import { getAddOns } from '../api/add-on-api';
import { getMainPage } from '../page-name';
import { getPublicOrganizations } from '../api/organization-api';
import { getRolesForEntity } from '../api/roles-api';
import { getTopicPopups } from '../api/popups-api';
import { palette } from '../../style/variables';
import pointCache from './point-cache';
import { property } from '../state/state-management/decorators/property';
import seoData from './seo-data';
import { state } from 'lit/decorators';
import translationData from './translation-data';

class TopicData extends State {
    @property({ type: Boolean }) isReady: boolean = false;

    @property({ type: Object }) topic?: GetTopicResponse;

    @property({ type: Object }) map?: google.maps.Map;

    @property({ value: [], type: Array }) searchBounds?: SearchBounds;

    @property({ type: String }) topicKey: string = '';

    @property({ type: String }) hasExceptions?: IntegrationException;

    @property({ type: Array }) activatedAddons: GetAddOnResponse[] = [];

    @property({ type: Array }) unactivatedAddons: GetAddOnResponse[] = [];

    @property({ type: Array }) allAddons: GetAddOnResponse[] = [];

    @property({ type: Array }) allChallenges: GetChallengeResponse[] = [];

    @property({ type: Array }) allElections: GetElectionResponse[] = [];

    @property({ type: Array }) standalonePopups: GetPopUpResponse[] = [];

    @property({ type: Object }) mobileDrawerFocusedPoint?: GetPointResponse;

    @property({ type: Boolean }) ballotEligible: boolean = true;

    @property({ type: String }) challengeId?: string;

    @property({ type: String }) activatedChallengeId?: string;

    @property({ type: String }) activatedChallengeKey?: string;

    @property() mapComponent?: MapComponent;

    @property() publicQRCode: string = '';

    @property() publicShareLink: string = '';

    @property() addOnsToInstall: GetAddOnResponse[] = [];

    @property() verifiedAlternateHost?: string;

    @property() organizationPassiveReferralCode?: string;

    @property() isOverwritePage: boolean = false;

    @property() categoryIconDataUrls: Map<string, string> = new Map();

    @state() intent?: IntentType = undefined;

    static amplitude_callsite: string = 'Topic Info Cache';

    // these getters are some convenience methods for accessing topic data. more can be added as needed
    get isWriter(): boolean {
        return this.topic?.is_writer === true || false;
    }

    get isAddPointer(): boolean {
        return this.topic?.is_addpointer === true || false;
    }

    get isOwner(): boolean {
        return this.topic?.is_owner === true || false;
    }

    get name(): string {
        return this.topic?.name ?? '';
    }

    get email(): string {
        return this.topic?.email ?? '';
    }

    get topicId(): string {
        return this.topic?._id.$oid ?? '';
    }

    get organizationId(): string | undefined {
        return this.topic?.organization_id?.$oid ?? undefined;
    }

    get discoveryLevel(): DiscoveryLevel {
        return this.topic?.discovery_level ?? DiscoveryLevel.Private;
    }

    get categories(): TopicCategory[] {
        return this.topic?.categories ?? [];
    }

    set topLevelUserTags(userTags: UserTag[]) {
        if (isDefined(this.topic)) {
            this.topic.tag_info.user_tags = userTags;
        }
    }

    get topLevelUserTags(): UserTag[] {
        return this.topic?.tag_info.user_tags ?? [];
    }

    get backgroundColor(): string {
        return this.topic?.header_color === '#fff' ? '#ffffff' : this.topic?.header_color ?? palette.primaryBlue;
    }

    get textColor(): string {
        const headerTextColor = this.topic?.header_text_color === '#fff' ? '#ffffff' : this.topic?.header_text_color;
        return headerTextColor && isTextColorVisible(this.backgroundColor, headerTextColor) ? headerTextColor : getVisibleTextColor(this.backgroundColor);
    }

    get accentColor(): string {
        return getDarkerColor(this.backgroundColor, this.textColor);
    }

    // note - this contains all subtags, not just the top level tags
    get allUserTags(): UserTag[] {
        return [...new Set(this.topic?.tag_info.user_tags.flatMap(t => { if(t.tags) { return [...t.tags];} return t;}))];
    }

    /** Resolve a category or tag/sub-tag id to its display name, recursing the
     *  full tag tree — unlike `allUserTags`, which flattens one level and drops
     *  parent groups, so it can't name a group or a deep sub-tag. */
    categoryOrTagNameById(oid: string | undefined): string | undefined {
        if (!isDefined(oid)) return undefined;
        const category = this.categories.find((c) => c._id?.$oid === oid);
        if (isDefined(category)) return category.name;
        const search = (tags: UserTag[]): string | undefined => {
            for (const tag of tags) {
                if (tag._id?.$oid === oid) return tag.name;
                const nested = tag.tags && tag.tags.length > 0 ? search(tag.tags) : undefined;
                if (isDefined(nested)) return nested;
            }
            return undefined;
        };
        return search(this.topLevelUserTags);
    }

    get postPointSubmissionPopUp() {
      return this.topic?.pop_ups?.find((popUp) => popUp.pop_up_type === PopUpTypeOld.PostPointSubmission && (popUp.is_active || this.isWriter && popUp.is_active));
    }

    get creatorProfiles(){
        return this.topic?.creator_profiles ?? [];
    }

    get hasChallenge() {
        return this.allChallenges.length > 0;
    }

    // be super careful with this, it impacts how we get points
    get challengeHappeningNow() {
        // since prosperity is only using the API, we dont want to show stuff here unless the user is already logged in
        if (!this.activatedChallengeId && topicData.hasExceptions === IntegrationException.ProsperityMarket) {
            return undefined;
        }
        for (const challenge of this.allChallenges) {
            if (this.isChallengeHappening(challenge)) {
                return challenge;
            }
        }
    }

    get hasElection() {
        return this.allElections.length > 0;
    }

    get emailCapturePopUp() {
        return this.topic?.pop_ups?.find((popUp) => popUp.pop_up_type === PopUpTypeOld.LeadGeneration && (popUp.is_active || this.isWriter));
    }

    get electionHappeningNow() {
        for (const election of this.allElections) {
            if (electionVotingIsHappening(election) || electionNominationsAreHappening(election) || electionResultsAreLive(election)) {
                return election;
            }
        }
        return undefined;
    }

    get activeElection() {
        return this.allElections.find((election) => election.active);
    }

    // call this at the top of any page that needs topic data
    // pattern is to call this function and when it resolves, and wrap all components that need topic data in a conditional on `topicData.isReady`

    async instantiateTopicData(amplitudeCallsite: string, isAuthenticated: boolean, addTelemetryView: boolean = true, useCache: boolean = false, topicId?: string, topicKey?: string, isOverwritePage: boolean = false, mapComponent?: MapComponent) {
        const queryString = getQueryStringParameters();
        if (!topicId || !topicKey) {
            // if topic_id and topic_key are not provided, get them from the url
            topicId = topicId ?? queryString.get('topic_id') ?? this.topicId;
            topicKey = topicKey ?? queryString.get('topic_key') ?? this.topicKey;
        }
        // Per-point edit links (#point) authorize the topic load via point_key when topic_key is missing.
        // See server: get_topic point_key fallback.
        const urlPointId = queryString.get('point_id') ?? undefined;
        const urlPointKey = queryString.get('point_key') ?? undefined;
        if (topicId === undefined) {
            window.location.assign('/#404');
            // set page name as 404
            return;
        }

        let hasError = false;
        try {
            const topic = await getTopic(amplitudeCallsite, topicId, topicKey, useCache, urlPointId, urlPointKey);
            this.topic = topic;
            this.topicKey = isDefined(topicKey) ? topicKey : '';

            // an overwrite page is a page where we want it to appear to an admin as if they are a regular user so we overwrite the data
            // We also want to load the enabled features for viewers on these pages because the orgs have paid for advanced access
            // this right now [Jun 2025] is just Command Center and Public Print Page
            this.isOverwritePage = isOverwritePage;

            if (this.isOverwritePage && topic.enabled_features) {
                // Dont block features on certain pages if the owning Organization has a subscription
                featuresInfo.setEnabledFeaturesForViewers(topic.enabled_features);
            }

            if (this.isOverwritePage && topic.is_writer) {
                // for admins make it look like they are a regular user
                this.overrideAdminAccessForPage();
            }

            this.mapComponent = mapComponent ?? this.mapComponent ?? undefined;

            // Challenges/elections may be unauthorized for a point_key-authenticated viewer (e.g. unique edit link
            // recipient on a now-private topic). Failures here must not block the topic load.
            try {
                const allChallenges = await getChallenges(TopicData.amplitude_callsite, topicId, topicKey, this.activatedChallengeId, this.activatedChallengeKey);
                this.allChallenges = allChallenges ?? [];
            } catch (challengeErr) {
                console.warn('Could not load challenges; continuing without them.', challengeErr);
                this.allChallenges = [];
            }
            try {
                const allElections = await getElections(TopicData.amplitude_callsite, topicId, topicKey);
                this.allElections = allElections ?? [];
            } catch (electionErr) {
                console.warn('Could not load elections; continuing without them.', electionErr);
                this.allElections = [];
            }

            this.isReady = true;
        } catch (e: any) {
            console.error(e);
            hasError = true;
            if (isAuthenticated) {
                window.location.replace('/#unauthorized');
            } else {
                const message = 'We could not verify your access to the page you requested.  Please log in to try again or contact the map owner for access.';
                window.location.replace(
                    `/#login?callback_url=${encodeURIComponent(window.location.href)}&message=${encodeURIComponent(message)}`
                );
            }
        }

        if (addTelemetryView) {
            // DO NOT add await here, this will cause the entire page to fail to load if the telemetry server is having issues
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            addTopicView(amplitudeCallsite, topicId, this.topic?.organization_id?.$oid);
        }

        // Perform any additional setup after the topic data is loaded.

        this.checkForExceptions();
        this.loadCategoryIconsAsDataUrls();

        // get additional info that shouldnt kill the whole page if it errors
        // temp removal of get add ons to not kill the page with the changes
        this.allAddons = [];
        if (isDefined(this.topic)) {
            await this.instantiateActiveAddOns(this.topic);
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            seoData.setPageTitle(amplitudeCallsite, this.topic);
        }

        // Resolve the custom domain BEFORE any URL replacement or view redirect,
        // so that swapUrlForOrgUrl has verifiedAlternateHost available when the
        // hashchange triggers child pages to mount (e.g. challenge-page).
        if (this.topic?.organization_id) {
           await this.setAlternateHost();
        }

        const view = queryString.get('view');

        // replace url after we've tested for any errors
        const newUrlData = this.getUrl();
        if (newUrlData.url !== undefined && !hasError && !view) {
            // set page url state and title
            replaceStateWithPageName(newUrlData.url, newUrlData.title ?? this.name);
        } else if (view && newUrlData.url !== undefined) {
            redirectToPage(newUrlData.url, view);
        } else if (isDefined(undefinedIfEmpty(view)) && !hasError && view) {
            const currentUrl = new URL(window.location.href);
            redirectToPage(currentUrl.origin + currentUrl.pathname, view);
        }

        await this.getCommandCenterShareLink();

        if (this.allChallenges.length > 0) {
            // Get the id of the topic's current challenge.
            this.challengeId = this.allChallenges.find((challenge) => challenge.is_live || challenge.is_registration_active)?._id.$oid ?? this.allChallenges[0]._id.$oid;

            // Get any activated challenge id and key from the local storage.
            const activatedChallengeStorageId = localStorage.getItem(`activated_challenge_${this.challengeId}_id`);
            const activatedChallengeStorageKey = localStorage.getItem(`activated_challenge_${this.challengeId}_write_key`);

            // Set the activated challenge data from the query string, or the local storage as a fallback.
            this.activatedChallengeId = queryString.get('activated_challenge_id') ?? activatedChallengeStorageId ?? challengeData.activatedChallengeId;
            this.activatedChallengeKey = queryString.get('activated_challenge_key') ?? activatedChallengeStorageKey ?? challengeData.activatedChallengeKey;

            // Make sure that any activated challenges we ever get from any source are also persisted to the local storage.
            if (isValidString(this.activatedChallengeId) && isValidString(this.activatedChallengeKey)) {
                localStorage.setItem(`activated_challenge_${this.challengeId}`, 'true');
                localStorage.setItem(`activated_challenge_${this.challengeId}_id`, this.activatedChallengeId);
                localStorage.setItem(`activated_challenge_${this.challengeId}_write_key`, this.activatedChallengeKey);
            }

            // Ensure the latest challenge data is pulled from the server.
            await challengeData.instantiateChallengeData(this.challengeId, this.topicKey, this.activatedChallengeId, this.activatedChallengeKey);
        }
    }

    async instantiateActiveAddOns(topic: GetTopicResponse) {
        if (this.topic?.is_writer === false) {
            return;
        }
        this.allAddons = await getAddOns(TopicData.amplitude_callsite);
        this.allAddons = this.allAddons.filter((addon) => addon.is_public);
        const topic_active_add_ons = topic.active_add_ons ?? [];
        this.activatedAddons = [];
        this.unactivatedAddons = [];

        // // TESTING for testing add on, add temp_addon constant to list
        // this.allAddons.push(temp_addon);

        if (this.hasExceptions === IntegrationException.HighStreetSafari) {
            this.allAddons.push(high_street_treasure_hunt_add_on);
        }

        this.allAddons.forEach((addon) => {
            const isActive = topic_active_add_ons.some((a: ObjectId) => a.$oid === addon._id.$oid);

            if (isActive) {
                this.activatedAddons.push(addon);
            } else {
                this.unactivatedAddons.push(addon);
            }
        });

        // TESTING for testing add on, add temp_addon constant to list
        // this.activatedAddons.push(temp_addon);
        if (this.hasExceptions === IntegrationException.HighStreetSafari && !this.activatedAddons.some((addon) => addon._id.$oid === high_street_treasure_hunt_add_on._id.$oid)) {
            this.activatedAddons.push(high_street_treasure_hunt_add_on);
        }

        // set the addons on the topic so next time a change is made it is "locked in"
        if (this.topic && this.activatedAddons.length > 0) {
            this.topic.active_add_ons = this.activatedAddons.map((addon) => addon._id);
        }
    }

    overrideAdminAccessForPage() {
        switch (this.topic?.key_level) {
            case KeyLevel.Write:
                // no overwriting because we are using a write key
                break;
            case KeyLevel.AddPoint:
                this.topic.is_owner = false;
                this.topic.is_writer = false;
                break;
            case KeyLevel.Read:
                this.topic.is_owner = false;
                this.topic.is_writer = false;
                this.topic.is_addpointer = false;
                break;
        }
    }

    // call this once the map is instantiated and available, right now this just happens in map-component
    instantiateBounds(map: google.maps.Map) {
        this.map = map;
        this.refreshBounds();
        setInterval(this.refreshBounds.bind(this), 10000);
    }

    refreshBounds(timeDelay: boolean = true) {
        const bounds = this.map?.getBounds();
        if (bounds !== undefined) {
            this.searchBounds = {
                east: bounds.getNorthEast().lng(),
                west: bounds.getSouthWest().lng(),
                north: bounds.getNorthEast().lat(),
                south: bounds.getSouthWest().lat(),
            };
        }
    }

    async getNewChallenges() {
        this.allChallenges = await getChallenges(TopicData.amplitude_callsite, this.topicId, this.topicKey, this.activatedChallengeId, this.activatedChallengeKey) ?? [];
    }

    getUrl(): { url: string | undefined, title: string | undefined } {
        const url = new URL(window.location.href);
        const hashData = url.hash.split('?')[0].substring(1); // remove the '#'

        let newUrlBase: string | undefined;
        let newTitle: string | undefined;
        const name = this.topic?.share_settings?.page_title ?? this.topic?.name ?? '';
        switch (hashData) {
            case 'command-center':
                newTitle = name;
                this.topic?.is_addpointer === true && !this.topic?.is_writer ?
                    newUrlBase = this.topic.cc_addpoint_link :
                    this.topic?.discovery_level === DiscoveryLevel.ReadPublic ?
                        newUrlBase = this.topic.cc_keyless_link :
                        newUrlBase = this.topic?.cc_read_link;
                break;
            case 'admin-portal':
                newTitle = `Edit: ${name}`;
                accountInfo.isAuthenticated ?
                    newUrlBase = this.topic?.cc_account_admin_link :
                    newUrlBase = this.topic?.cc_write_link;
                break;
            case 'discover-details':
                newTitle = `${name?? ''}: Interactive Guide`;
                newUrlBase = this.topic?.discover_details_link;
                break;
            default:
        }

        if (newUrlBase === undefined) {
            return { url: undefined, title: undefined };
        }

        // avoid historyAPI errors
        // Extract the base URL from the current URL
        const currentBaseUrl = new URL(window.location.href).hostname;

        // Extract the base URL from newUrlBase
        const newUrlBaseObj = new URL(newUrlBase);
        const newUrlBaseBaseUrl = newUrlBaseObj.hostname;

        // If the base URLs are not the same, replace the base URL in newUrlBase
        if (currentBaseUrl !== newUrlBaseBaseUrl) {
            newUrlBase = newUrlBase.replace(newUrlBaseBaseUrl, currentBaseUrl);
        }

        // get all params after # and the ?
        const params = url.hash.split('?')[1];
        // remove topic_id and topic_key params
        const paramsToRemove = ['topic_id', 'topic_key'];
        const paramsArray = params?.split('&').filter((param) => {
            const paramKey = param.split('=')[0];
            return !paramsToRemove.includes(paramKey);
        });

        const newParams = paramsArray?.join('&');
        const newUrl = new URL(newUrlBase);
        newUrl.search = newParams;

        // set favicon
        const categoryWithIcon = this.categories.find((category) => {
            return category.icon?.startsWith('http');
        });
        try {
            setFavicon(this.topic?.share_settings?.favicon ?? categoryWithIcon?.icon ?? '');
        } catch (e: any) {
        }

        return { url: newUrl.href, title: newTitle };
    }

    async installAddon(amplitude_callsite: string, addon?: GetAddOnResponse, refresh: boolean = true) {
        if (addon?.availability === AddOnAvailability.GetNotified || addon?.availability === AddOnAvailability.ComingSoon) {

            logWithAmplitude(amplitude_callsite, 'Get Notified Selected', { 'addonType': addon.title, 'email': accountInfo.email ?? topicData.email });

            getMainPage().errorToastElement.showToast(`We'll let you know when ${addon.title} is available!`);
        } else if (addon?.availability === AddOnAvailability.Available) {
            const topic_add_ons = topicData.topic?.active_add_ons ?? [];
            // if the addon is already active, dont add it again
            if (!topic_add_ons.some((a: ObjectId) => a.$oid === addon._id.$oid)) {
                topic_add_ons.push(addon._id);
            }
            let topic_hidden_add_ons = topicData.topic?.hidden_add_ons ?? [];
            topic_hidden_add_ons = topic_hidden_add_ons.filter((a: ObjectId) => a.$oid !== addon._id.$oid);
            await this.fetchAndUpdateTopicData({ active_add_ons: topic_add_ons, hidden_add_ons: topic_hidden_add_ons }, amplitude_callsite, refresh, refresh);
            if (refresh) {
                await getMainPage().reload(`The add-on has been added! You can view it on the left side menu.`);
            }
        }
    }

    async uninstallAddon(amplitude_callsite: string, addon?: GetAddOnResponse) {
        if (addon !== undefined) {
            let topic_add_ons = topicData.topic?.active_add_ons ?? [];
            topic_add_ons = topic_add_ons.filter((a: ObjectId) => a.$oid !== addon._id.$oid);
            const topic_hidden_add_ons = topicData.topic?.hidden_add_ons ?? [];
            // if the addon is already hidden, dont add it again
            if (!topic_hidden_add_ons.some((a: ObjectId) => a.$oid === addon._id.$oid)) {
                topic_hidden_add_ons.push(addon._id);
            }
            await this.fetchAndUpdateTopicData({ active_add_ons: topic_add_ons, hidden_add_ons: topic_hidden_add_ons }, amplitude_callsite);
            await getMainPage().reload();
        }
    }

    updateTopicData(topic: GetTopicResponse) {
        this.topic = topic;
    }

    async refetchTopic(amplitudeCallsite: string) {
        try {
            const newTopic = await getTopic(
                amplitudeCallsite,
                topicData.topicId,
                topicData.topicKey,
                false,
            );

            this.topic = newTopic;

            if (this.isOverwritePage && newTopic.is_writer) {
                this.overrideAdminAccessForPage();
            }

            return newTopic;
        } catch (e: any) {
            throw new Error(e);
        }
    }

    async loadStandalonePopups(topicId: string) {
        try {
            if (!topicId) return;
            this.standalonePopups = await getTopicPopups(topicId);
        } catch (e: any) {
            console.error('Failed to load standalone popups:', e);
            this.standalonePopups = [];
        }
    }

    // depending on where you are implemting this, you may need to refetch the topic before you call this to not overwrite any changes
    // e.g.await topicData.refetchTopic(MapComponent.amplitude_callsite);
    // e.g this.createTopicRequest = createTopicRequestFromTopic(topicData.topic);
    async fetchAndUpdateTopicData(topicRequest: Partial<CreateTopicRequest>, amplitudeCallsite: string, refreshMap: boolean = false, refreshMarkers: boolean = false) {

        if (!this.isReady || !this.topic) {
            getMainPage().errorToastElement.showToastAndThrow('An error has occurred.  Context: topic data is not ready');
        }

        const existingTopicData = createTopicRequestFromTopic(this.topic);

        // Merge the partial request with the existing data
        const fullTopicRequest: CreateTopicRequest = {
            ...existingTopicData,
            ...topicRequest,
        };

        try {
            await updateTopic(
                amplitudeCallsite,
                topicData.topicId,
                fullTopicRequest,
                topicData.topicKey
            );
        } catch (e: any) {
            getMainPage().errorToastElement.showToastAndThrow(`An error has occurred updating the map. ${JSON.stringify(topicRequest)}`);
        }

        try {
            const newTopic = await getTopic(
                amplitudeCallsite,
                topicData.topicId,
                topicData.topicKey,
                false,
            );

            this.topic = newTopic;
            await this.instantiateActiveAddOns(newTopic);
            this.loadCategoryIconsAsDataUrls();

            if (this.isOverwritePage && newTopic.is_writer) {
                this.overrideAdminAccessForPage();
            }

            // if topic request included any string type, initiate a refetch
            if (Object.values(topicRequest).some((value) => typeof value === 'string')) {
                translationData.originalTextChanged();
            }

            if (refreshMap && this.mapComponent) {
                await this.mapComponent.refreshMap();
            }

            if (refreshMarkers) {
                await pointCache.instantiatePointCache(newTopic, topicData.topicKey, true);
            }

            logWithGoogleTagAndTopicOrganization(amplitudeCallsite, GoogleTagEvent.TOPIC_UPDATED);

            return newTopic;
        } catch (e: any) {
            throw new Error(e);
        }
    }

    async createNewUserTag(isTagGroup: boolean = false, amplitudeCallsite?: string, name?: string) {
        // get random color from default colors
        const randomColor = DEFAULT_TAG_COLORS[Math.floor(Math.random() * DEFAULT_TAG_COLORS.length)];

        const newUserTag: TagRequest = {
            _id: undefined,
            name: name ?? 'New Tag',
            tags: undefined,
            icon: randomColor,
        };

        if (isTagGroup) {
            newUserTag.tags = [{
                _id: undefined,
                name: 'New Sub Tag',
                icon: randomColor,
            }];
        }

        const updatedUserTags = [...this.topLevelUserTags, newUserTag];

        const newTagInfo = {
            user_tags: updatedUserTags,
            google_tags: this.topic?.tag_info.google_tags,
        };

        await this.fetchAndUpdateTopicData({
            tag_info: newTagInfo
        }, amplitudeCallsite ?? TopicData.amplitude_callsite, true, true);

        this.topLevelUserTags = this.topic?.tag_info.user_tags ?? [];

        // return the last tag
        return this.topLevelUserTags.slice(-1)[0];
    }

    async refreshTopicViewerEntitlements() {
        if (this.isOverwritePage && this.topic && this.topic.enabled_features) {
            // Dont block features on certain pages if the owning Organization has a subscription
            featuresInfo.setEnabledFeaturesForViewers(this.topic.enabled_features);
            if (featuresInfo.publiclyViewableEnabled && this.mapComponent) {
                await this.mapComponent.unlockMap();
            }
        }
    }

    async fetchAndUpdateUserTag(userTagId: string, userTagRequest: Partial<TagRequest>, amplitudeCallsite: string) {
        const updatedUserTags = this.topLevelUserTags.map((tag) => {
            if (tag._id?.$oid === userTagId) {
                // if update is icon, update all subtags with the same icon
                if (userTagRequest.icon) {
                    tag.tags?.forEach((subTag) => {
                        subTag.icon = userTagRequest.icon ?? tag.icon;
                    });
                }
                return {
                    ...tag,
                    ...userTagRequest,
                };
            } else {
                return tag;
            }
        });

        const newTagInfo = {
            user_tags: updatedUserTags,
            google_tags: this.topic?.tag_info.google_tags,
        };

        await this.fetchAndUpdateTopicData({
            tag_info: newTagInfo
        }, amplitudeCallsite, false, true);

        this.topLevelUserTags = this.topic?.tag_info.user_tags ?? [];
        await pointCache.syncPointTags(pointCache.allPoints, this.topLevelUserTags);
    }

    async deleteTag(userTagId: string, amplitudeCallsite: string) {
        const updatedUserTags = this.topLevelUserTags.filter((tag) => tag._id?.$oid !== userTagId);

        const newTagInfo = {
            user_tags: updatedUserTags,
            google_tags: this.topic?.tag_info.google_tags,
        };

        await this.fetchAndUpdateTopicData({
            tag_info: newTagInfo
        }, amplitudeCallsite, false, true);

        this.topLevelUserTags = this.topic?.tag_info.user_tags ?? [];

        await pointCache.syncPointTags(pointCache.allPoints, this.topLevelUserTags);
    }

    async createNewUserSubTag(userTagId: string, amplitudeCallsite?: string) {
        const userTag = this.topLevelUserTags.find((tag) => tag._id?.$oid === userTagId);
        const newUserSubTag: TagRequest = {
            _id: undefined,
            name: 'New Sub Tag',
            icon: userTag?.icon ?? DEFAULT_TAG_COLORS[Math.floor(Math.random() * DEFAULT_TAG_COLORS.length)],
        };

        const existingSubTags = this.topLevelUserTags.find((tag) => tag._id?.$oid === userTagId)?.tags ?? [];

        const updatedUserTags = this.topLevelUserTags.map((tag) => {
            if (tag._id?.$oid === userTagId) {
                return {
                    ...tag,
                    tags: [...existingSubTags, newUserSubTag],
                };
            } else {
                return tag;
            }
        });

        const newTagInfo = {
            user_tags: updatedUserTags,
            google_tags: this.topic?.tag_info.google_tags,
        };

        await this.fetchAndUpdateTopicData({
            tag_info: newTagInfo
        }, amplitudeCallsite ?? TopicData.amplitude_callsite, false, true);

        this.topLevelUserTags = this.topic?.tag_info.user_tags ?? [];

        // return the last subtag
        return this.topLevelUserTags.find((tag) => tag._id?.$oid === userTagId)?.tags?.slice(-1)[0];
    }

    async fetchAndUpdateUserSubTag(userTagId: string, userSubTagId: string, userSubTagRequest: Partial<UserTag>, amplitudeCallsite: string) {
        const updatedUserTags = this.topLevelUserTags.map((tag) => {
            if (tag._id?.$oid === userTagId) {
                return {
                    ...tag,
                    tags: tag.tags?.map((subTag) => {
                        if (subTag._id?.$oid === userSubTagId) {
                            return {
                                ...subTag,
                                ...userSubTagRequest,
                            };
                        } else {
                            return subTag;
                        }
                    }),
                };
            } else {
                return tag;
            }
        });

        const newTagInfo = {
            user_tags: updatedUserTags,
            google_tags: this.topic?.tag_info.google_tags,
        };

        await this.fetchAndUpdateTopicData({
            tag_info: newTagInfo
        }, amplitudeCallsite, false, true);

        this.topLevelUserTags = this.topic?.tag_info.user_tags ?? [];
        await pointCache.syncPointTags(pointCache.allPoints, this.topLevelUserTags);
    }

    async deleteSubTag(userTagId: string, userSubTagId: string, amplitudeCallsite: string) {
        const updatedUserTags = this.topLevelUserTags.map((tag) => {
            if (tag._id?.$oid === userTagId) {
                return {
                    ...tag,
                    tags: tag.tags?.filter((subTag) => subTag._id?.$oid !== userSubTagId),
                };
            } else {
                return tag;
            }
        });

        const newTagInfo = {
            user_tags: updatedUserTags,
            google_tags: this.topic?.tag_info.google_tags,
        };

        await this.fetchAndUpdateTopicData({
            tag_info: newTagInfo
        }, amplitudeCallsite, false, true);

        this.topLevelUserTags = this.topic?.tag_info.user_tags ?? [];
        await pointCache.syncPointTags(pointCache.allPoints, this.topLevelUserTags);
    }

    async createNewCategory(amplitudeCallsite?: string) {
        const newCategory: TopicCategory = {
            _id: undefined,
            name: 'New Category',
            icon: DEFAULT_CATEGORY_ICON,
            can_crowdsource: true,
            icon_size: undefined,
        };

        const updatedCategories = [...this.categories, newCategory];

        await this.fetchAndUpdateTopicData({
            categories: updatedCategories,
        }, amplitudeCallsite ?? TopicData.amplitude_callsite, false, true);

        // return the last category
        return this.categories.slice(-1)[0];
    }

    async createNewCategories(categories: TopicCategory[], amplitudeCallsite?: string) {

        const updatedCategories = [...this.categories, ...categories];

        await this.fetchAndUpdateTopicData({
            categories: updatedCategories,
        }, amplitudeCallsite ?? TopicData.amplitude_callsite, false, true);

    }

    async checkForCategoryExceptions(category: TopicCategory) {
        if (topicData.hasExceptions === IntegrationException.WelcomeHomeRelocations) {
            // if the category or tag name contains "Rental Property", throw an error
            if (category.name?.toLowerCase().includes('rental property')) {
                getMainPage().errorToastElement.showToastAndThrow('You cannot edit or delete this Category, it is set by WelcomeHome Relocations');
            }
        }
    }

    async fetchAndUpdateCategory(categoryId: string, categoryRequest: Partial<TopicCategory>, amplitudeCallsite: string) {
        const category = this.getCategory(categoryId);
        if (!category) {
            throw new Error('Category not found');
        }

        await this.checkForCategoryExceptions(category);

        const updatedCategory = {
            ...category,
            ...categoryRequest,
        };

        const updatedCategories = this.categories.map((c) => {
            if (c._id?.$oid === categoryId) {
                return updatedCategory;
            } else {
                return c;
            }
        });

        await this.fetchAndUpdateTopicData({
            categories: updatedCategories,
        }, amplitudeCallsite, true, true);

    }

    async deleteCategory(categoryId: string, amplitudeCallsite: string) {
        const updatedCategories = this.categories.filter((category) => category._id?.$oid !== categoryId);

        // delete all points with this category
        const pointsToDelete = pointCache.allPoints.filter((point) => point.topic_category_id.$oid === categoryId);
        for (const point of pointsToDelete) {
            await deletePoint(amplitudeCallsite, topicData.topicId, point._id.$oid, topicData.topicKey);
        }

        await this.fetchAndUpdateTopicData({
            categories: updatedCategories,
        }, amplitudeCallsite, true, true);

    }

    getCategory(categoryId: string) {
        return this.categories.find((category) => category._id?.$oid === categoryId);
    }

    async fetchAndAddChallenge(createChallengeRequest: CreateChallengeRequest, amplitudeCallsite: string) {
        try {
            const response = await addChallenge(amplitudeCallsite, topicData.topicId, createChallengeRequest, topicData.topicKey);
            await challengeData.instantiateChallengeData(response._id.$oid, topicData.topicKey);
        } catch (e: any) {
            throw new Error(e);
        }

        await this.getNewChallenges();

        return challengeData.challenge;
    }

    async fetchAndUpdateChallenge(challenge_id: string, createChallengeRequest: Partial<CreateChallengeRequest>, amplitudeCallsite: string) {
        try {
            const challenge = this.allChallenges.find((challenge) => challenge._id.$oid === challenge_id);

            if (!isDefined(challenge)) {
                getMainPage().errorToastElement.showToastAndThrow('Challenge not found, cannot update');
                return;
            }

            const latest_challenge = createChallengeRequestFromGetChallengeResponse(challenge);
            const fullChallengeRequest: CreateChallengeRequest = {
                ...latest_challenge,
                ...createChallengeRequest,
            };
            await updateChallenge(amplitudeCallsite, challenge_id, fullChallengeRequest, topicData.topicKey);

            // if challengedata is already instantiated, update it
            await challengeData.refreshChallengeData();
        } catch (e: any) {
            throw new Error(e);
        }

        await this.getNewChallenges();
        return challengeData.challenge;
    }

    async fetchAndUpdateElection(election_id: string | undefined, createElectionRequest: Partial<CreateElectionRequest>, amplitudeCallsite: string) {
        // to handle duplicate cases... we need to check in electio_id is not defined but there are elections
        if (!isDefined(election_id) && this.allElections.length > 0) {
            getMainPage().errorToastElement.showToastAndThrow('Election ID is not defined, but there are elections available. Please contact support.');
            return;
        }
        try {
            if (election_id) {
                const election = this.allElections.find((election) => election._id.$oid === election_id);
                if (!isDefined(election)) {
                    getMainPage().errorToastElement.showToastAndThrow('Election not found, cannot update');
                    return;
                }
                const createElectionRequestFull: CreateElectionRequest = {
                    ...createElectionRequestFromGetElectionResponse(election),
                    ...createElectionRequest,
                };
                await updateElection(amplitudeCallsite, topicData.topicId, election_id, createElectionRequestFull, topicData.topicKey);
            } else {
                const createElectionRequestFull: CreateElectionRequest = {
                    ...dummyInitialElection,
                    ...createElectionRequest
                };
                await addElection(amplitudeCallsite, topicData.topicId, createElectionRequestFull, topicData.topicKey);
            }
        } catch (e: any) {
            getMainPage().errorToastElement.showToastAndThrow('An error occurred updating the election');
        }

        this.allElections = await getElections(TopicData.amplitude_callsite, topicData.topicId, topicData.topicKey);
        return this.allElections.find((election) => election._id.$oid === election_id);
    }

    get hasActiveGate(): boolean {
        const gates = this.topic?.gates;
        if (!Array.isArray(gates)) {
            return false;
        }
        return gates.some((gate) => {
            if ('Password' in gate && isDefined(gate.Password) && gate.Password.is_active) {
                return true;
            }
            if ('Email' in gate && isDefined(gate.Email) && gate.Email.is_active) {
                return true;
            }
            return false;
        });
    }

    // helper function to get the details on the password ad email gates
    async activeGateDetails(gate_type: GateType, gate_id?: String): Promise<PasswordGateDetails | EmailGateDetails | GateDetails | undefined> {
        const gates = this.topic?.gates?.filter((gate) =>
            gate_type === GateType.Password
                    ? isDefined((gate as GetTopicPasswordGateResponse).Password) && (gate as GetTopicPasswordGateResponse).Password.is_active
                    : gate_type === GateType.Email
                        ? isDefined((gate as GetTopicEmailGateResponse).Email) && (gate as GetTopicEmailGateResponse).Email.is_active
                        : false
        )
            ?? undefined;

        if (!gates) {
            return undefined;
        }

        // create array of PaywallGates.Payment objects from paywallGates

      const gateDetails: GateDetails[] = [];
        if (gate_type === GateType.Password || !isDefined(gate_type)) {
            gates.forEach((gate) => {
                if (isDefined((gate as GetTopicPasswordGateResponse).Password)) {
                    gateDetails.push((gate as GetTopicPasswordGateResponse).Password);
                }
            });
        } else if (gate_type === GateType.Email || !isDefined(gate_type)) {
            gates.forEach((gate) => {
                if (isDefined((gate as GetTopicEmailGateResponse).Email)) {
                    gateDetails.push((gate as GetTopicEmailGateResponse).Email);
                }
            }
            );
        }

        // filter out any gates that are not active
        const activeGates = gateDetails.filter((gate) => gate.is_active);

        if (activeGates.length === 0) {
            return undefined;
        }

        // if gate_id is not provided, return the first active gate
        if (!gate_id) {
            return activeGates[0];
        }

        // if the gate_id that is provided is not active, return first active gate
        if (!activeGates.some((gate) => gate._id.$oid === gate_id)) {
            return activeGates[0];
        }

        // if gate_id is provided, return the gate with that id
        const gate = activeGates.find((gate) => gate._id.$oid === gate_id);
        return gate;
    }

    async getTopicRoles() {
        if (!this.isReady || !this.topicId || !accountInfo.isAuthenticated || !topicData.isWriter) {
            return [];
        }
        return await getRolesForEntity(
            MapComponent.amplitude_callsite,
            this.topicId,
            EntityType.Topic
        );
    }

    // helper to handle exceptions
    // these are often used for specific needs for integrations
    // e.g. if a user is not logged in, we might want to redirect them to a login page
    checkForExceptions() {
        if (this.organizationId === '65b013941ee1516b823a6218' && (this.isWriter || this.isAddPointer) && !accountInfo.isAuthenticated) {
            // when a reloction specialist is given the write link, we want to give them access to some features and simplify their editor portal.
            this.hasExceptions = IntegrationException.WelcomeHomeRelocations;
            featuresInfo.setEnabledFeaturesForViewers(this.topic?.enabled_features ?? []);
        }

        if (this.organizationId === '650b5c6a1f763989dde13dfb') {
            this.hasExceptions = IntegrationException.JLLRealEstate;
        }

        if (this.organizationId === '65aab8bb0cef1bf37e96d43c') {
            this.hasExceptions = IntegrationException.AustraliaMeatAndLivestock;
        }

        if (this.topicId === '65f1bbcdaaa02088663b5c12' || this.topicId === '6894b7df6fd8ad147248eaef') {
            this.hasExceptions = IntegrationException.TheGoSolution;
        }

        if (this.topicId === '65fb233f75f237755bf06df3' || this.topicId === '6606f905bb97fb053741795e') {
            this.hasExceptions = IntegrationException.TrudiFord;
        }

        if (this.topicId === '65f9d47932fd2d0604075846') {
            this.hasExceptions = IntegrationException.DefHero;
        }

        if (this.topicId === '66171ea4fc36b7b2ae993479') {
            this.hasExceptions = IntegrationException.Telco;
        }

        if (this.topicId === '663c0a18979d516e9bc28ec4' || this.topicId === '663c071c979d516e9bc28bab') {
            this.hasExceptions = IntegrationException.WilliamEScooter;
        }

        if (this.topic?.organization_id?.$oid === '6662236907150617ec16e805') {
            this.hasExceptions = IntegrationException.ProsperityMarket;
        }

        if (this.topic?.organization_id?.$oid === '669044b9884c02a5505d377a') {
            this.hasExceptions = IntegrationException.SeattleGood;
        }

        if (this.topic?.organization_id?.$oid === '684db2b263c50067c284fdf0') {
            this.hasExceptions = IntegrationException.TeamBuildingAnywhere;
        }

        if (this.topic?.organization_id?.$oid === '691717bbb0d8e2360db454aa') {
            this.hasExceptions = IntegrationException.HighStreetSafari;
        }

        if (this.topicId === '69b44bc586364279ff89a08c') {
            this.hasExceptions = IntegrationException.Roswell;
        }

        if (this.topic?.organization_id?.$oid === '678551c30cfdd38cf5190b96') {
            this.hasExceptions = IntegrationException.FranchiseDemographics;
        }
    }

    isChallengeHappening(challenge: GetChallengeResponse) {
        return (challenge.is_live ?? false);
    }

    async setAlternateHost() {
        if (topicData.topic?.organization_id !== undefined) {
          const organization = (await getPublicOrganizations(TopicData.amplitude_callsite, [topicData.topic.organization_id.$oid]))[0];
            if (organization.cname?.validated === true && organization.cname?.dns_verified) {
                this.verifiedAlternateHost = organization.cname.custom_url;
            }
            // Store the organization's referral code for the "Built with Proxi Maps" button
            this.organizationPassiveReferralCode = organization.referral_code;
        }
    }

    async getCommandCenterShareLink() {
        // Always share a keyed link so the link keeps working even if the owner
        // later changes the map's discovery level from searchable to unlisted.
        // Addpoint link for collaborative maps, read link otherwise.
        let link: string | undefined = this.topic?.is_addpointer === true
            ? this.topic?.cc_addpoint_link
            : this.topic?.cc_read_link;

        // if the org has a custom domain, use that and replace it in the path
        if (this.verifiedAlternateHost) {
            // replace the domain with the custom domain
            link = link?.replace(new URL(link).hostname, this.verifiedAlternateHost);
        }

        this.publicShareLink = link ?? '';

        // SET QR CODE
        this.publicQRCode = await getQRCode(TopicData.amplitude_callsite, link ?? '');
    }

    swapUrlForOrgUrl(url: string) {
        // Use verifiedAlternateHost if available, otherwise fall back to
        // the current hostname when we're already on a custom domain.
        const customHost = this.verifiedAlternateHost ?? this.getCurrentCustomHost();
        if (!customHost) {
            return url;
        }
        try {
            const base = new URL(url);
            base.hostname = customHost;
            return base.toString();
        } catch {
            return url;
        }
    }

    /**
     * Returns the current hostname if it's a custom domain (not Proxi infrastructure),
     * or undefined if we're on a standard Proxi host.
     */
    private getCurrentCustomHost(): string | undefined {
        const host = window.location.hostname;
        if (host.endsWith('.proxi.co') || host === 'proxi.co'
            || host.endsWith('.azurewebsites.net') || host === 'localhost') {
            return undefined;
        }
        return host;
    }

    private loadCategoryIconsAsDataUrls() {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        (async () => {
            // New map.
            const iconMap = new Map<string, string>();

            // Fire off a task for each category to fetch its icon as a data URL.
            const tasks = this.categories.map(async c => {
                try {
                    const dataUrl = await urlToDataUrl(c.icon);
                    return [c._id?.$oid ?? '', dataUrl];
                } catch (error) {
                    if (c.icon.startsWith('/i/')) {
                        return  [c._id?.$oid ?? '',  c.icon]; // If the icon is a local path, return it directly.
                    } else {
                        console.error(`Failed to load icon for category ${c.name || 'unknown'}:`, error);
                        const random_color = DEFAULT_CATEGORY_COLORS[Math.floor(Math.random() * DEFAULT_CATEGORY_COLORS.length)];
                        return [c._id?.$oid ?? '', makeColorIntoDataSvg(random_color)]; // Fallback to default icon if loading fails
                    }
                }
            });

            // Wait for all tasks to complete.
            const results = await Promise.all(tasks);

            // Add the results to the map, filtering out any empty data URLs.
            results.forEach(([id, dataUrl]) => {
                if (isDefined(dataUrl) && isDefined(id)) {
                    iconMap.set(id, dataUrl);
                }
            });

            // Set the categoryIconDataUrls property to the map of data URLs.
            this.categoryIconDataUrls = iconMap;
        })();
    }


    // Intent calls for onboarding

    getIntentIcon(intent: IntentType): string {
        const icons: Record<IntentType, string> = {
            [IntentType.Map]: '🗺️',
            [IntentType.Guide]: '📍',
            [IntentType.Passport]: '🎯',
            [IntentType.Hunt]: '🔍',
            [IntentType.Event]: '📅',
            [IntentType.BestOf]: '🏆',
        };
        return icons[intent] || '📍';
    }

    getIntentTitle(intent: IntentType): string {
        const titles: Record<IntentType, string> = {
            [IntentType.Map]: 'Interactive and Embeddable Map',
            [IntentType.Guide]: 'App-Like Guide',
            [IntentType.Passport]: 'Passport / Check-in Challenge',
            [IntentType.Hunt]: 'Scavenger Hunt',
            [IntentType.Event]: 'Event Map',
            [IntentType.BestOf]: 'Best Of / Top Picks List',
        };
        return titles[intent] || intent;
    }

    getIntentDescription(intent: IntentType): string {
        const descriptions: Record<IntentType, string> = {
            [IntentType.Map]: 'Customizable and crowdsourceable map to showcase locations, routes, and stories. Add paywalls, passwords, and email gates to create exclusive experiences',
            [IntentType.Guide]: 'Curated collection of locations for your audience to sign up for, save favorites, get coupons on, and explore as an interactive guide and app-link experience',
            [IntentType.Passport]: 'Interactive challenge for your audience with check-ins and rewards to encourage foot traffic and local engagement',
            [IntentType.Hunt]: 'Quest-based experience with clues and missions where users try to find hidden locations',
            [IntentType.Event]: 'Time-specific or itinerary map for conferences, festivals, tours, or gatherings',
            [IntentType.BestOf]: 'A community voting experience where users can submit and vote on their favorite locations to create a dynamic "best of" list',
        };
        return descriptions[intent] || '';
    }

    getSetupVideoLink(intent: IntentType): string {
        const videoLinks: Record<IntentType, string> = {
            [IntentType.Map]: 'https://www.proxi.co/proxi-demo-video',
            [IntentType.Guide]: 'https://www.youtube.com/watch?v=lkK4VgPpMek',
            [IntentType.Passport]: 'https://www.loom.com/share/459c93c1e1754fe8940ee300455fe5a4?sid=9423a309-ab53-4290-b222-f84b94b6f1f1',
            [IntentType.Hunt]: 'https://drive.google.com/file/d/17G94WDGjgP3s2ogSLzcuvciMM0oEpDFf/view',
            [IntentType.Event]: 'https://www.proxi.co/proxi-demo-video',
            [IntentType.BestOf]: 'https://www.loom.com/share/dd1ebbc436b04895af0cde0b5cf71846?sid=61b9685c-8974-4593-add8-d287052b77d2',
        };
        return videoLinks[intent] || '';
    }
}

const topicData = new TopicData();

export default topicData;
